// (c) 2023 ABBYY Development, Inc.

import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ActionButton,
    Box,
    Divider,
    FlexContainer,
    IconButton,
    Link,
    ModalDeleteDialog,
    Toolbar,
    ToolbarGroup,
    Typography,
    AddFill,
    CheckFill,
    CrossFill,
    Delete,
    Table
} from 'components';
import { useBoolean, useTable, useDeleteTemplates } from 'hooks';
import { TEMPLATES_QUERY_KEY } from 'hooks/query-keys';
import { Template } from 'types/template';
import AddTemplateDialog from '../AddTemplate.dialog';
import ScheduleIndicator from '../shared/ScheduleIndicator';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useNotification } from 'libs/notification';

export interface TemplateListProps {
    templates: Template[];
    currentPage: number;
    pageCount: number;
    setCurrentPage: Dispatch<SetStateAction<number>>;
}

const TemplateList = ({
    templates,
    currentPage,
    pageCount,
    setCurrentPage
}: TemplateListProps) => {
    const history = useHistory();
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { success, error } = useNotification();

    const [templatesToDelete, setTemplatesToDelete] = useState<Template[]>([]);
    const [isDeleteDialogOpen, showDeleteDialog, hideDeleteDialog] =
        useBoolean();
    const [isAddDialogOpen, showAddDialog, hideAddDialog] = useBoolean();
    const { mutate } = useDeleteTemplates();

    const deleteTemplates = () => {
        mutate(templatesToDelete, {
            onSuccess: (_, deletedTemplates) => {
                success(
                    t('expressions.delete_templates_success', {
                        deletedTemplates: deletedTemplates
                            .map((template) => template.name)
                            .join(', ')
                    })
                );
            },
            onError: () => {
                error(t('expressions.delete_templates_failed'));
            },
            onSettled: () => {
                hideDeleteDialog();
                queryClient.invalidateQueries(TEMPLATES_QUERY_KEY);
            }
        });
    };

    const table = useTable<Template>({
        columns: useMemo(
            () => [
                {
                    Header: t('expressions.template_name'),
                    accessor: 'name',
                    Cell: (row) => (
                        <Link
                            onClick={() =>
                                history.push(
                                    `/templates/${encodeURIComponent(
                                        row.value
                                    )}`
                                )
                            }
                        >
                            {row.value}
                        </Link>
                    ),
                    filter: 'text',
                    width: 100
                },
                {
                    Header: t('general.Added'),
                    accessor: 'creationTime',
                    Cell: (row) => <>{new Date(row.value).toLocaleString()}</>,
                    filter: 'date'
                },
                {
                    Header: t('general.Recorders'),
                    accessor: 'recordersCount',
                    filter: 'numberRange',
                    defaultMin: 0,
                    defaultMax: 1000,
                    align: 'center'
                },
                {
                    Header: t('expressions.record_on_assignment'),
                    accessor: 'recordOnStart',
                    Cell: (row) => (
                        <Box display="flex" justifyContent="center">
                            {row.value ? (
                                <CheckFill color="primary" />
                            ) : (
                                <CrossFill color={['grey', 'A200']} />
                            )}
                        </Box>
                    ),
                    filter: 'boolean'
                },
                {
                    Header: t('expressions.scheduled_recording'),
                    accessor: 'schedule',
                    id: 'scheduleEnabled',
                    Cell: ({ row }) => (
                        <ScheduleIndicator schedule={row.original.schedule} />
                    ),
                    filter: 'boolean'
                },
                {
                    Header: t('expressions.whitelist'),
                    accessor: 'whitelistCount',
                    filter: 'numberRange',
                    defaultMin: 0,
                    defaultMax: 100,
                    align: 'center'
                },
                {
                    Header: t('expressions.blacklist'),
                    accessor: 'blacklistCount',
                    filter: 'numberRange',
                    defaultMin: 0,
                    defaultMax: 100,
                    align: 'center'
                },
                {
                    Header: '',
                    accessor: 'id',
                    id: 'action',
                    Cell: ({ row }) => (
                        <IconButton
                            onClick={() => {
                                setTemplatesToDelete([row.original]);
                                showDeleteDialog();
                            }}
                            icon={
                                <Delete
                                    color={
                                        row.original.default
                                            ? 'disabled'
                                            : 'error'
                                    }
                                />
                            }
                            isDisabled={row.original.default}
                        />
                    ),
                    disableSortBy: true,
                    disableFilters: true
                }
            ],
            [history, showDeleteDialog, t]
        ),
        data: templates,
        hasSelect: true,
        disableSelectAll: true,
        hasSort: true
    });

    return (
        <>
            <ModalDeleteDialog
                isOpen={isDeleteDialogOpen}
                titleText={
                    templatesToDelete.length > 1
                        ? t('expressions.delete_templates')
                        : t('expressions.delete_template')
                }
                onClose={hideDeleteDialog}
                onDelete={deleteTemplates}
            >
                <Typography variant="body1">
                    {templatesToDelete.length > 1
                        ? t('delete_modal.delete_items')
                        : t('delete_modal.delete_item')}
                </Typography>
            </ModalDeleteDialog>

            <AddTemplateDialog
                open={isAddDialogOpen}
                onRequestClose={hideAddDialog}
            />
            <FlexContainer>
                <Toolbar isRounded={false}>
                    <ToolbarGroup align="right">
                        <ActionButton
                            icon={<AddFill color="primary" />}
                            onClick={showAddDialog}
                        >
                            {t('expressions.add_template')}
                        </ActionButton>
                        <Divider spacing={0} height={22} />
                        <ActionButton
                            isDisabled={!table.selectedFlatRows.length}
                            onClick={() => {
                                showDeleteDialog();
                                setTemplatesToDelete(
                                    table.selectedFlatRows.map(
                                        (row) => row.original
                                    )
                                );
                            }}
                            icon={
                                <Delete
                                    color={
                                        !table.selectedFlatRows.length
                                            ? 'disabled'
                                            : 'error'
                                    }
                                />
                            }
                        >
                            {t('expressions.delete_selected')}
                        </ActionButton>
                    </ToolbarGroup>
                </Toolbar>
                <Table<Template>
                    resourceId="TEMPLATE_LIST"
                    currentPage={currentPage}
                    pageCount={pageCount}
                    setCurrentPage={setCurrentPage}
                    controller={table}
                />
            </FlexContainer>
        </>
    );
};

export default TemplateList;
